import React from 'react';
import { TextInput } from '@gafe/common-ui';
import merge from 'classnames';
import trim from 'lodash/trim';

import { Dropdown } from '@/components/generic/Dropdown/Dropdown';
import { DropdownItem } from '@/components/generic/Dropdown/DropdownItem';
import { DropdownMenu } from '@/components/generic/Dropdown/DropdownMenu';
import { DropdownToggle } from '@/components/generic/Dropdown/DropdownToggle';
import { Image } from '@/components/generic/Image';
import { GoogleAddressDetails, GoogleAddressSuggestion } from '@/types';

import { AutocompleteService, generateSessionToken, PlacesService } from './helpers';
import { Props, State } from './types';

import styles from './AddressLineWithSuggestionInput.module.scss';

export class AddressLineWithSuggestionInput extends React.Component<Props, State> {
  state: State = {
    predictionsList: [],
    isPredictionsListVisible: false,
    sessionToken: generateSessionToken(),
  };

  openSuggestionsList = () => {
    this.setState({ isPredictionsListVisible: true });
  };

  closeSuggestionsList = () => {
    this.setState({
      isPredictionsListVisible: false,
      sessionToken: generateSessionToken(),
    });
  };

  clearSuggestionsList = () => {
    this.setState({ predictionsList: [] });
  };

  fetchAddressSuggestions = (value: string) => {
    // Look for predictions only when input value is not an empty string
    if (value.length === 0) {
      return;
    }

    try {
      AutocompleteService().getPlacePredictions(
        {
          componentRestrictions: { country: 'us' },
          input: value,
          sessionToken: this.state.sessionToken,
          types: ['address'],
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            this.setState({
              isPredictionsListVisible: true,
              predictionsList: predictions.map(prediction => ({
                description: prediction.description,
                placeId: prediction.place_id,
              })),
            });
          }
        },
      );
    } catch (error) {
      this.closeSuggestionsList();
      this.clearSuggestionsList();
      console.error(error);
    }
  };

  onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    this.props.onChange(event);
    this.fetchAddressSuggestions(value);

    // Close and clear suggestion list and
    if (value.length === 0) {
      this.closeSuggestionsList();
      this.clearSuggestionsList();
    }
  };

  onSuggestionItemClick = (item: GoogleAddressSuggestion) => () => {
    PlacesService().getDetails(
      {
        placeId: item.placeId,
        sessionToken: this.state.sessionToken,
      },
      (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const addressDetails: GoogleAddressDetails = {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zipCode: '',
          };

          (result?.address_components || []).forEach(addressComponent => {
            const value = trim(addressComponent.long_name);

            switch (addressComponent.types[0]) {
              case 'postal_code':
                addressDetails.zipCode = value;
                break;

              case 'locality':
                addressDetails.city = value;
                break;

              case 'administrative_area_level_1':
                addressDetails.state = trim(addressComponent.short_name);
                break;

              case 'street_number':
                addressDetails.addressLine1 = merge(value, addressDetails.addressLine1);
                break;

              case 'route':
                addressDetails.addressLine1 = merge(addressDetails.addressLine1, value);
                break;
            }
          });

          this.props.onSuggestionSelect(addressDetails);
          this.clearSuggestionsList();
        }
      },
    );
  };

  render() {
    const { isPredictionsListVisible, predictionsList } = this.state;
    const {
      onChange,
      onSuggestionSelect,
      inputContainerClassName,
      colorSchema,
      classes,
      style,
      ...props
    } = this.props;

    return (
      <Dropdown
        className={merge(
          styles.dropdown,
          inputContainerClassName,
          props.label ? styles.with_label : styles.without_label,
        )}
        isOpen={isPredictionsListVisible && predictionsList.length > 0}
        toggle={this.closeSuggestionsList}
      >
        <TextInput
          classes={{
            wrapper: style === 'row' ? styles.input_wrapper_large : styles.input_wrapper,
          }}
          {...props}
          onChange={this.onTextInputChange}
          onFocus={this.openSuggestionsList}
          placeholder={props.placeholder}
          testId={props['data-testid']}
          value={props.value}
        />

        <DropdownToggle className={styles.dropdown_toggle} tabIndex={-1} />

        <DropdownMenu itemsSize="big">
          <div className={styles.predictions_list}>
            {predictionsList.map(item => (
              <DropdownItem
                className={styles.predictions_list_item}
                key={item.placeId}
                onClick={this.onSuggestionItemClick(item)}
              >
                {item.description}
              </DropdownItem>
            ))}
          </div>

          <div className={styles.google_logo}>
            <Image
              alt="Powered by Google logo"
              src="/assets/images/powered_by_google/desktop/on_white.png"
            />
          </div>
        </DropdownMenu>
      </Dropdown>
    );
  }
}
